import { Header, Footer } from '@/shared/components/layout';
import { useDocumentTitle } from '@/shared/hooks';
import { Hero21, ProcessSteps, CaseStudyForm } from './Components';
import { ProcessStepsSolution } from './Components/ProcessStepsSolution';
import { TryItFree } from '@/features/Pricing/Components/TryItFree';

const ResultsPage = () => {
  useDocumentTitle(
    'Results & Case Studies - BrokerMax Real Estate Lead Generation'
  );

  return (
    <div className="min-h-screen">
      <Header variant="light" />
      <main>
        <Hero21
          title="How Shamairah Noufaro leveraged BrokerMax to drive 584 buyer/rental leads from 177 listings"
          // backgroundImage="/images/hero-21.jpg"
        />
        {/* Partner Logos Section */}
        <section className="bg-white py-12 px-4 overflow-hidden">
          <div className="container mx-auto max-w-7xl">
            <div className="relative overflow-hidden">
              {/* Scrolling container */}
              <div className="flex items-center gap-8 opacity-30 partner-scroll whitespace-nowrap">
                {/* First set of logos */}
                {[
                  { src: '/images/realtor-canada.png', alt: 'REALTOR.ca' },
                  { src: '/images/brokerbay.png', alt: 'BrokerBay' },
                  { src: '/images/canada.jpg', alt: 'Canada' },
                  { src: '/images/proptx.png', alt: 'PropTx', padding: '10px' },
                  { src: '/images/crea.png', alt: 'CREA' }
                ].map((logo, i) => (
                  <img
                    key={`first-${i}`}
                    src={logo.src}
                    alt={logo.alt}
                    className="h-12 object-contain flex-shrink-0"
                    style={{ padding: logo.padding }}
                  />
                ))}
                {/* Duplicate set for seamless loop */}
                {[
                  { src: '/images/realtor-canada.png', alt: 'REALTOR.ca' },
                  { src: '/images/brokerbay.png', alt: 'BrokerBay' },
                  { src: '/images/canada.jpg', alt: 'Canada' },
                  { src: '/images/proptx.png', alt: 'PropTx', padding: '10px' },
                  { src: '/images/crea.png', alt: 'CREA' }
                ].map((logo, i) => (
                  <img
                    key={`second-${i}`}
                    src={logo.src}
                    alt={logo.alt}
                    className="h-12 object-contain flex-shrink-0"
                    style={{ padding: logo.padding }}
                  />
                ))}
              </div>
            </div>
          </div>
          <style>{`
            @keyframes partnerScroll {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(-50%);
              }
            }
            .partner-scroll {
              animation: partnerScroll 20s linear infinite;
              display: flex;
              width: max-content;
            }
            @media (max-width: 768px) {
              .partner-scroll {
                animation: partnerScroll 8s linear infinite;
              }
            }
          `}</style>
        </section>
        <ProcessSteps
          steps={[
            {
              number: 1,
              title: 'Rising Google & Facebook Ads costs',
              description:
                'Shamairah was spending $2,500/month on Google Ads with a mere 3% conversion rate. She needed 100 clicks to get 3 leads, and only 1 of those 3 would actually pick up the phone.'
            },
            {
              number: 2,
              title: 'LoneWolf Leads were not working',
              description:
                'Shamairah tried LoneWolf Leads (BoldLeads) for 4 months and spent $8,800. Out of 87 leads received, less than 10% of them even answered the phone or even remembered filling out a form on a random website.'
            },
            {
              number: 3,
              title: 'Economy was slow',
              description:
                'Agents who relied on "hustle" alone were struggling. Shamairah needed deal flow she could count on, even during down markets or the slowest months of winter.'
            }
          ]}
          image="/images/showings.png"
          imageAlt="BrokerMax results dashboard"
        />

        {/* Full Case Study CTA */}
        <CaseStudyForm />

        <hr className="my-20 border-t-2 border-gray-400" />
        <ProcessStepsSolution
          steps={[
            {
              number: 1,
              title: '30+ active listings, any given month',
              description:
                'BrokerMax delivered 25-30 FSBO (For Sale By Owner) listing opportunities to Shamairah every single month. These were not cold leads — they were property owners who had already decided to sell and filled out a form requesting help getting their property on Realtor.ca. Shamairah posted these as "mere listings" - no commission to her upfront.'
            },
            {
              number: 2,
              title: 'Reduced lead generation costs by 42%',
              description:
                'By switching from paid advertising to BrokerMax, Shamairah replaced her expensive, unprofitable monthly advertising expense with a flat-rate subscription to BrokerMax.'
            },
            {
              number: 3,
              title: 'Built a team, during the worst market in years',
              description:
                'BrokerMax gave Shamairah something most solo agents never have: the confidence to hire during one of the toughest markets in Canadian real estate history (July 2025 to January 2026). While interest rates hit 6.5% and sales volume dropped 31%, most agents were cutting staff. Shamairah was hiring. Before BrokerMax, hiring felt like a gamble. "What if I bring on an agent and do not have enough deals to keep them busy?" This fear kept her working 60-hour weeks, handling everything herself. But once she saw consistent monthly lead flow—even during the slowest months of winter—the math became clear: She was leaving commissions on the table by NOT hiring.'
            }
          ]}
          image="/images/tablet-01.png"
          imageAlt="BrokerMax results dashboard"
        />
        <CaseStudyForm />
        {/* Testimonial Section with Image Background */}
        <section className="bg-gray-100 py-20 px-4 relative overflow-hidden">
          {/* Parallax Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center bg-fixed bg-no-repeat"
            style={{
              backgroundImage:
                'url(https://easywebinar-site.easywebinar.com/images/amy-porter-image.jpg)',
              backgroundAttachment: 'fixed',
              backgroundPosition: 'center',
              backgroundSize: 'cover'
            }}
          ></div>

          <div className="container mx-auto max-w-7xl relative z-10">
            {/* Testimonial Box and Image Layout */}
            <div className="flex flex-col lg:flex-row items-start gap-8">
              {/* Testimonial Box - Left Side */}
              <div
                className="w-full lg:w-2/3 rounded-lg p-8 lg:p-12 relative"
                style={{ backgroundColor: 'rgba(0, 27, 47, 0.72)' }}
              >
                {/* Star Rating */}
                <div className="flex mb-6">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-6 h-6 text-yellow-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-xl text-white mb-8 leading-relaxed">
                  &ldquo; I was a solo agent burning thousands on Google Ads,
                  lead brokers and direct mail... BrokerMax literally changed
                  everything for me. I even had to build my team of 3 agents
                  almost overnight because it was too much for me to handle on
                  my own.
                  <span className="font-bold">
                    {' '}
                    Now, I always have at least 30 listings up every month, even
                    in the &quot;slow&quot; winter. But the real magic is the
                    buyer and rental leads... Search my name on REALTOR.ca and
                    see my active listings for yourself.
                  </span>
                  &rdquo;
                </p>

                {/* Attribution */}
                <div className="mb-6">
                  <div
                    data-nosnippet
                    className="text-blue-400 text-xl font-semibold mb-2"
                  >
                    Shamairah Noufaro
                  </div>
                  <div className="text-gray-300 text-sm">
                    Real estate agent in Ontario
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <TryItFree />
      </main>
      <Footer />
    </div>
  );
};

export default ResultsPage;
